import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Logo } from "@/components/Logo";
import { LanguageToggle } from "@/components/LanguageToggle";
import { WhatsAppAssistant } from "@/components/WhatsAppAssistant";
import { LocationSheet } from "@/components/LocationSheet";
import { ServiceAreaBanner } from "@/components/ServiceAreaBanner";
import { ServiceSearch } from "@/components/ServiceSearch";
import { useLiveRefresh } from "@/lib/useLiveRefresh";
import { useGeo, geoLabel } from "@/lib/geo";
import { CustomerShell } from "@/components/CustomerShell";
import { ModeSwitch } from "@/components/ModeSwitch";
import { serviceIcon, rupee } from "@/lib/constants";
import {
    MapPin, Bell, Menu, House, Wrench, Wallet, BadgeCheck, ReceiptIndianRupee,
    Clock, LifeBuoy, Star, ShieldCheck, ArrowRight, X, Scale, LogOut,
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1580835919673-429540898832?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";
const SOLUTIONS_IMG = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";
const SUPPORT = { phone: "+91 88583 88788", tel: "tel:+918858388788", email: "support@homiie.in", hours: "9 AM – 8 PM, every day" };

const HERO_SLIDES = [
    { img: HERO_IMG, title: "Professional Home Services for a Better Home Life", sub: "Verified Professionals. Safe. Affordable." },
    { img: "https://images.unsplash.com/photo-1646640381839-02748ae8ddf0?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", title: "Repairs & Installations, Done Right", sub: "Trained experts. Transparent pricing." },
    { img: "https://images.unsplash.com/photo-1665242043190-0ef29390d289?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", title: "Every Home. Every Service.", sub: "One trusted app for your whole home." },
];

const TRUST = [
    { icon: BadgeCheck, label: "Verified Professionals" },
    { icon: ReceiptIndianRupee, label: "Transparent Pricing" },
    { icon: Clock, label: "Pick Your Time Slot" },
    { icon: ShieldCheck, label: "Secure Payments" },
    { icon: LifeBuoy, label: "Support 9 AM–8 PM" },
];

export default function Home() {
    const [services, setServices] = useState([]);
    const [areas, setAreas] = useState([]);
    const [homes, setHomes] = useState([]);
    const [unread, setUnread] = useState(0);
    const [offers, setOffers] = useState([]);
    const [search, setSearch] = useState("");
    const [menuOpen, setMenuOpen] = useState(false);
    const [locOpen, setLocOpen] = useState(false);
    const { user } = useAuth();
    const { t } = useLang();
    const nav = useNavigate();
    const geo = useGeo();

    useEffect(() => {
        const sc = new URLSearchParams(window.location.search).get("society");
        if (sc) { localStorage.setItem("homiie_society", sc.toUpperCase()); nav(`/society/${sc.toUpperCase()}`, { replace: true }); }
    }, []); // eslint-disable-line

    useEffect(() => {
        api.get("/services").then((r) => setServices(r.data.filter((s) => (s.active_area_ids || []).length > 0))).catch(() => {});
        api.get("/service-areas").then((r) => setAreas(r.data)).catch(() => {});
        api.get("/offers").then((r) => setOffers(r.data || [])).catch(() => {});
        if (user?.role === "customer") {
            api.get("/homes").then((r) => setHomes(r.data)).catch(() => {});
            api.get("/notifications").then((r) => setUnread((r.data || []).filter((n) => !n.read).length)).catch(() => {});
        }
    }, [user]);

    const goBook = (serviceId) => nav(serviceId ? `/book?service=${serviceId}` : "/book");
    useLiveRefresh(() => {
        api.get("/services").then((r) => setServices(r.data.filter((s) => (s.active_area_ids || []).length > 0))).catch(() => {});
        api.get("/service-areas").then((r) => setAreas(r.data)).catch(() => {});
    });
    const location = areas.length ? areas.map((a) => a.cluster || a.name).slice(0, 2).join(" · ") : "Jodhpur";

    const categories = [];
    const seenCat = {};
    services.forEach((s) => { if (!seenCat[s.category]) { seenCat[s.category] = 1; categories.push(s); } });

    const home0 = homes[0];

    return (
        <CustomerShell>
            {/* Header */}
            <header className="bg-white px-5 pb-3 pt-4 shadow-[0_1px_0_rgba(16,42,67,0.06)]">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <button data-testid="home-menu-btn" aria-label="Open menu" onClick={() => setMenuOpen(true)} className="rounded-xl p-1.5 text-navy transition-colors hover:bg-navy/5"><Menu size={22} /></button>
                        <Logo size={30} showText={false} />
                        <div>
                            <p className="font-heading text-base font-extrabold leading-none text-navy">HOMIIE</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            data-testid="home-location-pill"
                            onClick={() => setLocOpen(true)}
                            className="flex items-center gap-1 rounded-full bg-navy/5 px-2.5 py-1.5 text-[11px] font-semibold text-navy transition-colors hover:bg-navy/10"
                        >
                            <MapPin size={12} className="text-teal" /> <span className="max-w-[110px] truncate">{geoLabel(geo) || location}</span>
                        </button>
                        <button
                            data-testid="header-notif-btn"
                            aria-label="Notifications"
                            onClick={() => nav(user?.role === "customer" ? "/notifications" : "/login")}
                            className="relative rounded-full bg-navy/5 p-2.5 text-navy transition-colors hover:bg-navy/10"
                        >
                            <Bell size={17} />
                            {unread > 0 && <span data-testid="notif-badge" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-saffron px-1 text-[9px] font-bold text-white">{unread}</span>}
                        </button>
                    </div>
                </div>
                <ModeSwitch full className="mt-3" />
            </header>

            <ServiceAreaBanner onChangeLocation={() => setLocOpen(true)} />

            {/* Hero carousel (image-first) */}
            <div className="px-5 pt-4">
                <HeroCarousel onBook={() => goBook()} />
            </div>

            {/* Trust row */}
            <div className="px-5 pt-5">
                <div className="grid grid-cols-5 gap-1.5">
                    {TRUST.map((tr) => {
                        const Icon = tr.icon;
                        return (
                            <div key={tr.label} className="flex flex-col items-center gap-1.5 text-center">
                                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-aqua-light text-teal-dark"><Icon size={18} strokeWidth={2} /></span>
                                <span className="text-[9px] font-semibold leading-tight text-navy/65">{tr.label}</span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Search */}
            <div className="relative px-5 pt-5">
                <ServiceSearch services={services} value={search} onChange={setSearch} onPick={goBook} placeholder={t("Search for a service (e.g. AC, Electrician)")} />
            </div>

            {/* Category strip */}
            <div className="pt-6">
                <div className="flex items-center justify-between px-5">
                    <h2 className="font-heading text-lg font-bold text-navy">{t("Categories")}</h2>
                    <button data-testid="see-all-categories" onClick={() => goBook()} className="text-xs font-semibold text-teal">{t("See All")} →</button>
                </div>
                <div className="mt-3 flex gap-4 overflow-x-auto px-5 pb-1 homiie-scroll">
                    {categories.slice(0, 10).map((s) => {
                        const Icon = serviceIcon(s.icon);
                        return (
                            <button key={s.category} data-testid={`category-${s.category}`} onClick={() => goBook(s.id)} className="flex shrink-0 flex-col items-center gap-1.5">
                                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-aqua-light">
                                    <Icon size={22} className="text-teal-dark" strokeWidth={2} />
                                </span>
                                <span className="w-16 truncate text-center text-[11px] font-medium text-navy/70">{s.name.replace(" Visit", "")}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Popular services */}
            <div className="pt-6">
                <div className="flex items-center justify-between px-5">
                    <h2 className="font-heading text-lg font-bold text-navy">{t("Popular Services")}</h2>
                    <button data-testid="see-all-services" onClick={() => goBook()} className="text-xs font-semibold text-teal">{t("See All")} →</button>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3 px-5">
                    {services.slice(0, 8).map((s) => (
                        <button
                            key={s.id}
                            data-testid={`service-card-${s.category}`}
                            onClick={() => goBook(s.id)}
                            className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-white text-left shadow-[0_6px_20px_rgba(0,156,149,0.06)] transition-transform active:scale-[0.98]"
                        >
                            <div className="h-28 w-full overflow-hidden">
                                <img src={s.image_url} alt={s.name} className="h-full w-full object-cover" />
                            </div>
                            <div className="p-3">
                                <h3 className="truncate font-heading text-sm font-bold text-navy">{s.name.replace(" Visit", "")}</h3>
                                <p className="mt-0.5 text-xs font-semibold text-teal-dark">{s.price_label}</p>
                                {s.rating ? (
                                    <p className="mt-1 flex items-center gap-1 text-[11px] text-navy/60"><Star size={11} className="fill-saffron text-saffron" /> {s.rating}{s.rating_count ? ` (${s.rating_count})` : ""}</p>
                                ) : (
                                    <p className="mt-1 text-[11px] font-medium text-teal">New on HOMIIE</p>
                                )}
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Offers — redeemable on the final bill, conditions shown upfront */}
            {offers.length > 0 && (
                <div className="pt-6">
                    <div className="flex items-center justify-between px-5">
                        <h2 className="font-heading text-lg font-bold text-navy">{t("Offers for You")}</h2>
                        <span className="text-[11px] font-medium text-navy/45">Apply on your final bill</span>
                    </div>
                    <div className="mt-3 flex gap-3 overflow-x-auto px-5 pb-2 homiie-scroll">
                        {offers.map((o) => <OfferCard key={o.code} o={o} services={services} />)}
                    </div>
                </div>
            )}

            {/* Home Passport summary */}
            {home0 && (
                <div className="px-5 pt-6">
                    <div data-testid="home-passport-card" className="relative overflow-hidden rounded-3xl border border-teal/20 bg-gradient-to-br from-aqua-light to-white p-5">
                        <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-teal/10 blur-xl" />
                        <div className="relative flex items-center gap-2">
                            <ShieldCheck size={18} className="text-teal" />
                            <h3 className="font-heading text-sm font-bold uppercase tracking-wide text-teal-dark">Your Home Passport</h3>
                        </div>
                        <p className="relative mt-2 font-heading text-lg font-extrabold text-navy">{home0.label || "My Home"}</p>
                        <p className="relative text-xs font-semibold text-navy/50">{home0.home_id}</p>
                        <div className="relative mt-4 flex gap-2">
                            <button data-testid="passport-view-btn" onClick={() => nav(`/home/${home0.id}`)} className="flex-1 rounded-xl bg-teal py-2.5 text-xs font-bold text-white transition-transform active:scale-95">View Passport</button>
                            <button data-testid="passport-book-btn" onClick={() => nav(`/book?home=${home0.id}`)} className="flex-1 rounded-xl border border-teal/30 bg-white py-2.5 text-xs font-bold text-teal-dark">Book for this Home</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Complete Home Solutions */}
            <div className="px-5 pt-6">
                <button data-testid="home-solutions-banner" onClick={() => goBook()} className="relative block w-full overflow-hidden rounded-3xl text-left">
                    <img src={SOLUTIONS_IMG} alt="Complete home solutions" className="h-36 w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-r from-navy/85 to-navy/20" />
                    <div className="absolute inset-0 flex flex-col justify-center p-5 text-white">
                        <h3 className="font-heading text-lg font-extrabold">Complete Home Solutions</h3>
                        <p className="mt-0.5 text-xs text-white/80">Maintenance · Repairs · Interiors</p>
                        <span className="mt-3 inline-flex w-max items-center gap-1 rounded-full bg-teal px-4 py-1.5 text-xs font-bold">Explore <ArrowRight size={13} /></span>
                    </div>
                </button>
            </div>

            {/* Service coverage + support */}
            <CoverageAndSupport areas={areas} />

            {/* Footer links */}
            <div className="px-5 py-7">
                <button
                    data-testid="home-join-pro-btn"
                    onClick={() => nav("/join-pro")}
                    className="flex w-full items-center justify-center gap-2 rounded-2xl border border-border bg-white py-3.5 text-sm font-bold text-navy transition-colors hover:border-teal hover:text-teal"
                >
                    <Wrench size={16} /> {t("Become a HOMIIE Professional")}
                </button>
                <div className="mt-4 flex items-center justify-center gap-4 text-xs font-semibold text-navy/45">
                    <button data-testid="home-legal-link" onClick={() => nav("/legal")} className="hover:text-teal">{t("Legal & Policies")}</button>
                    <span>·</span>
                    <span>homiie.in</span>
                </div>
                <p className="mt-3 text-center text-[11px] text-muted">HOMIIE · Your Home, Managed.</p>
            </div>

            <MenuDrawer open={menuOpen} onClose={() => setMenuOpen(false)} nav={nav} user={user} />
            {locOpen && <LocationSheet accent="services" onClose={() => setLocOpen(false)} />}
        </CustomerShell>
    );
}

function HeroCarousel({ onBook }) {
    const [i, setI] = useState(0);
    const [rating, setRating] = useState(null);
    useEffect(() => {
        api.get("/public/rating-summary").then((r) => { if (r.data.count >= r.data.min_count_to_show) { setRating(r.data); } }).catch(() => {});
    }, []);
    useEffect(() => {
        const id = setInterval(() => setI((p) => (p + 1) % HERO_SLIDES.length), 4500);
        return () => clearInterval(id);
    }, []);
    const s = HERO_SLIDES[i];
    return (
        <div data-testid="home-hero" className="relative overflow-hidden rounded-3xl" style={{ boxShadow: "0 18px 40px -14px rgba(16,42,67,0.3)" }}>
            <img src={s.img} alt={s.title} className="h-56 w-full object-cover transition-opacity duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/45 to-navy/10" />
            <div className="absolute inset-0 flex flex-col justify-end p-5 text-white">
                {rating && (
                    <div data-testid="hero-rating" className="mb-2 inline-flex w-max items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1 backdrop-blur">
                        <Star size={12} className="fill-saffron text-saffron" />
                        <span className="text-[11px] font-bold">{rating.average}</span>
                        <span className="text-[10px] text-white/80">from {rating.count} customer ratings</span>
                    </div>
                )}
                <h1 className="font-heading text-xl font-extrabold leading-tight">{s.title}</h1>
                <p className="mt-1 text-xs text-white/85">{s.sub}</p>
                <button data-testid="hero-book-btn" onClick={onBook} className="mt-3 w-max rounded-full bg-white px-4 py-2 text-xs font-bold text-navy transition-transform active:scale-95">Book a Service</button>
            </div>
            <div className="absolute bottom-3 right-4 flex gap-1.5">
                {HERO_SLIDES.map((_, idx) => (
                    <button key={idx} aria-label={`Show slide ${idx + 1}`} data-testid={`hero-dot-${idx}`} onClick={() => setI(idx)} className={`h-1.5 rounded-full transition-all ${idx === i ? "w-5 bg-white" : "w-1.5 bg-white/50"}`} />
                ))}
            </div>
        </div>
    );
}

function OfferCard({ o, services }) {
    const cat = o.category && services.find((s) => s.category === o.category);
    const terms = [o.first_service_only && "First service only", cat && `${cat.name.replace(" Visit", "")} only`, o.min_amount > 0 && `Min bill ${rupee(o.min_amount)}`,
        o.valid_to && `Valid till ${o.valid_to}`, "1 per booking", "Not with multi-service discount"].filter(Boolean);
    return (
        <div data-testid={`offer-${o.code}`} className="relative flex w-72 shrink-0 items-center gap-3 overflow-hidden rounded-2xl border border-teal/20 bg-gradient-to-br from-aqua-light to-white p-4">
            <span className="absolute -left-1.5 top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-lightbg" />
            <span className="absolute -right-1.5 top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-lightbg" />
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-teal text-white">
                <span className="font-heading text-sm font-extrabold">{o.discount_type === "percent" ? `${o.discount_value}%` : `₹${o.discount_value}`}</span>
            </div>
            <div className="min-w-0 flex-1">
                <h3 className="truncate font-heading text-sm font-bold text-navy">{o.title}</h3>
                <p className="text-[10.5px] leading-snug text-navy/55" data-testid={`offer-terms-${o.code}`}>{terms.join(" · ")}</p>
                <span className="mt-1 inline-block rounded-md border border-dashed border-teal/40 bg-white px-2 py-0.5 text-[11px] font-bold tracking-wide text-teal-dark">{o.code}</span>
            </div>
        </div>
    );
}

function CoverageAndSupport({ areas }) {
    return (
        <div className="px-5 pt-6">
            <div data-testid="home-coverage" className="rounded-3xl border border-border bg-white p-5">
                <h2 className="flex items-center gap-1.5 font-heading text-base font-bold text-navy"><MapPin size={16} className="text-teal" /> Where we serve</h2>
                <p className="mt-1 text-xs text-navy/55">Home services are currently available only in these Jodhpur localities. Availability of each service is checked for your address before you pay.</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                    {areas.map((a) => <span key={a.id} data-testid={`coverage-area-${a.id}`} className="rounded-full bg-aqua-light px-2.5 py-1 text-[11px] font-semibold text-teal-dark">{a.name}</span>)}
                </div>
                <div data-testid="home-support" className="mt-4 border-t border-border pt-4 text-xs text-navy/70">
                    <p className="font-heading text-sm font-bold text-navy">Customer support</p>
                    <p className="mt-1">Services: {SUPPORT.hours}</p>
                    <p>HOMIIE Care: 24/7 only for emergencies</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                        <a data-testid="home-support-call" href={SUPPORT.tel} className="rounded-full bg-navy px-3.5 py-1.5 font-bold text-white">Call {SUPPORT.phone}</a>
                        <a data-testid="home-support-email" href={`mailto:${SUPPORT.email}`} className="rounded-full border border-navy/15 px-3.5 py-1.5 font-bold text-navy">{SUPPORT.email}</a>
                    </div>
                </div>
            </div>
        </div>
    );
}

function MenuDrawer({ open, onClose, nav, user }) {
    const { logout } = useAuth();
    if (!open) return null;
    const go = (to) => { onClose(); nav(to); };
    const Item = ({ icon: Icon, label, onClick, testid }) => (
        <button data-testid={testid} onClick={onClick} className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-semibold text-navy transition-colors hover:bg-navy/5">
            <Icon size={18} className="text-teal" /> {label}
        </button>
    );
    return (
        <div className="fixed inset-0 z-[60] flex" onClick={onClose}>
            <div className="h-full w-72 max-w-[80%] animate-slide-in bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2"><Logo size={28} showText={false} /><span className="font-heading font-extrabold text-navy">HOMIIE</span></div>
                    <button data-testid="menu-close-btn" onClick={onClose} className="rounded-full bg-navy/5 p-1.5"><X size={16} /></button>
                </div>
                <div className="mt-5 space-y-1">
                    <Item icon={House} label="My Homes" testid="menu-my-homes" onClick={() => go("/my-home")} />
                    <Item icon={Wallet} label="Payments & Wallet" testid="menu-payments" onClick={() => go("/payments")} />
                    <WhatsAppAssistant accent="services" drawerItemClass="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-semibold text-navy transition-colors hover:bg-navy/5" iconClass="text-teal" />
                    <Item icon={Wrench} label="Become a Professional" testid="menu-join-pro" onClick={() => go("/join-pro")} />
                    <Item icon={Scale} label="Legal & Policies" testid="menu-legal" onClick={() => go("/legal")} />
                </div>
                <div className="mt-4 border-t border-border pt-4">
                    <div className="mb-2 flex items-center justify-between px-3"><span className="text-xs font-semibold text-navy/50">Language</span><LanguageToggle /></div>
                    {user && <Item icon={LogOut} label="Logout" testid="menu-logout" onClick={() => { logout(); onClose(); nav("/login"); }} />}
                </div>
            </div>
        </div>
    );
}
